import {
  equipmentModelService,
  type EquipmentModel,
  type CreateEquipmentModelPayload,
} from '@/services/equipment-model.service';

/**
 * Single implementation of "persist an equipment model and its images".
 *
 * The image work cannot live inside `EquipmentModelForm` — uploads need the
 * model id, which only exists after the create/update round-trip — so it used
 * to sit in `EquipmentModelsPage.handleFormSubmit`. Phase C reuses the same
 * form from the intake workflow, and duplicating this sequence there would
 * mean two places to keep in step. It lives here instead, and both callers
 * use it.
 */
export interface EquipmentModelSaveInput {
  payload: CreateEquipmentModelPayload;
  pendingFiles: File[];
  removedImageIndices: number[];
  /** Model id when editing. Omit to create. */
  editTargetId?: string;
}

export async function saveEquipmentModel({
  payload,
  pendingFiles,
  removedImageIndices,
  editTargetId,
}: EquipmentModelSaveInput): Promise<EquipmentModel> {
  let model: EquipmentModel;

  if (editTargetId) {
    model = await equipmentModelService.update(editTargetId, payload);
    // Remove images in reverse index order so earlier indices stay valid.
    const sortedRemovals = [...removedImageIndices].sort((a, b) => b - a);
    for (const idx of sortedRemovals) {
      await equipmentModelService.deleteImage(editTargetId, idx);
    }
  } else {
    model = await equipmentModelService.create(payload);
  }

  for (const file of pendingFiles) {
    await equipmentModelService.uploadImage(model.id, file, file.name);
  }

  return model;
}
